"use client"

import { useState } from "react"

const AbacusIntroduction = ({ onScore, onFinish }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [selectedPart, setSelectedPart] = useState(null)
  const [showExplanation, setShowExplanation] = useState(false)

  const steps = [
    {
      title: "Добро пожаловать! 👋",
      content: "Давайте познакомимся с Абакусом!",
      instruction: "Нажми 'Далее', чтобы начать изучение",
    },
    {
      title: "Что такое Абакус? 🧮",
      content: "Абакус - это древний инструмент для счёта. Он помогает нам быстро считать!",
      instruction: "Посмотри на картинку абакуса",
    },
    {
      title: "Части Абакуса 🔍",
      content: "У абакуса есть важные части. Давай их изучим!",
      instruction: "Нажимай на разные части абакуса",
    },
    {
      title: "Верхние бусинки = 5 ⬆️",
      content: "Каждая верхняя бусинка означает число 5",
      instruction: "Нажми на верхнюю бусинку",
    },
    {
      title: "Нижние бусинки = 1 ⬇️",
      content: "Каждая нижняя бусинка означает число 1",
      instruction: "Нажми на нижние бусинки",
    },
    {
      title: "Разделительная линейка ➖",
      content: "Линейка разделяет верхние и нижние бусинки",
      instruction: "Посмотри на разделительную линейку",
    },
    {
      title: "Готов считать! 🎉",
      content: "Теперь ты знаешь, как устроен абакус!",
      instruction: "Нажми 'Завершить', чтобы закончить урок",
    },
  ]

  const abacusParts = [
    { id: "top-bead", name: "Верхняя бусинка", value: 5, description: "Каждая верхняя бусинка = 5" },
    { id: "separator", name: "Разделительная линейка", value: 0, description: "Разделяет верхние и нижние бусинки" },
    { id: "bottom-bead", name: "Нижняя бусинка", value: 1, description: "Каждая нижняя бусинка = 1" },
  ]

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
      setSelectedPart(null)
      setShowExplanation(false)
      onScore(10)
    } else {
      onFinish()
    }
  }

  const selectPart = (partId) => {
    setSelectedPart(partId)
    setShowExplanation(true)
    onScore(5)
  }

  const currentStepData = steps[currentStep]

  return (
    <div className="lesson-card card-sky">
      <h2 className="lesson-title" style={{ color: "#0c4a6e" }}>
        Знакомство с Абакусом 👋
      </h2>

      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <div style={{ fontSize: "1.125rem", color: "#4b5563", marginBottom: "8px" }}>
          Шаг {currentStep + 1} из {steps.length}
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}></div>
        </div>
      </div>

      {/* Текущий шаг */}
      <div className="step-content">
        <h3 className="step-title">{currentStepData.title}</h3>
        <p className="step-text">{currentStepData.content}</p>
        <p className="step-instruction">{currentStepData.instruction}</p>
      </div>

      {/* Интерактивный абакус */}
      {currentStep >= 1 && (
        <div className="abacus-container">
          <div className="abacus">
            <div className="abacus-frame">
              {/* Верхняя часть */}
              <div className="abacus-rod"></div>
              <div style={{ marginBottom: "16px" }}>
                <button
                  onClick={() => selectPart("top-bead")}
                  className={`abacus-bead top-bead ${selectedPart === "top-bead" ? "selected active" : ""}`}
                  style={{ display: "block", margin: "0 auto" }}
                />
                {currentStep >= 2 && (
                  <div style={{ fontSize: "0.75rem", textAlign: "center", marginTop: "4px", fontWeight: "bold" }}>
                    Верхняя бусинка (5)
                  </div>
                )}
              </div>

              {/* Разделительная линейка */}
              <button
                onClick={() => selectPart("separator")}
                className={`abacus-separator separator-button ${selectedPart === "separator" ? "selected" : ""}`}
              />
              {currentStep >= 2 && selectedPart === "separator" && (
                <div style={{ fontSize: "0.75rem", textAlign: "center", marginBottom: "8px", fontWeight: "bold" }}>
                  Разделительная линейка
                </div>
              )}

              {/* Нижние бусинки */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                {Array.from({ length: 4 }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => selectPart("bottom-bead")}
                    className={`abacus-bead bottom-bead ${selectedPart === "bottom-bead" ? "selected active" : ""}`}
                  />
                ))}
                {currentStep >= 2 && (
                  <div style={{ fontSize: "0.75rem", textAlign: "center", marginTop: "4px", fontWeight: "bold" }}>
                    Нижние бусинки (1)
                  </div>
                )}
              </div>

              <div className="abacus-rod" style={{ marginTop: "16px" }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Объяснение выбранной части */}
      {showExplanation && selectedPart && (
        <div className="explanation-box">
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "2rem", marginBottom: "8px" }}>✨</div>
            <h4 className="explanation-title">{abacusParts.find((p) => p.id === selectedPart)?.name}</h4>
            <p className="explanation-text">{abacusParts.find((p) => p.id === selectedPart)?.description}</p>
          </div>
        </div>
      )}

      {/* Специальные инструкции для разных шагов */}
      {currentStep === 2 && (
        <div className="special-instruction">
          <p style={{ textAlign: "center", fontSize: "1.125rem" }}>
            🎯 Нажимай на разные части абакуса, чтобы узнать о них больше!
          </p>
        </div>
      )}

      {currentStep === 3 && (
        <div className="special-instruction red">
          <div style={{ textAlign: "center" }}>
            <div className="special-icon">🔴</div>
            <p className="special-title">Верхняя бусинка = 5</p>
            <p className="special-text">Одна красная бусинка сверху означает пять единиц!</p>
          </div>
        </div>
      )}

      {currentStep === 4 && (
        <div className="special-instruction blue">
          <div style={{ textAlign: "center" }}>
            <div className="special-icon">🔵</div>
            <p className="special-title">Нижняя бусинка = 1</p>
            <p className="special-text">Каждая синяя бусинка снизу означает одну единицу!</p>
          </div>
        </div>
      )}

      {currentStep === 5 && (
        <div className="special-instruction gray">
          <div style={{ textAlign: "center" }}>
            <div className="special-icon">➖</div>
            <p className="special-title">Разделительная линейка</p>
            <p className="special-text">Она разделяет верхние бусинки (5) и нижние (1)!</p>
          </div>
        </div>
      )}

      {/* Кнопки навигации */}
      <div className="navigation-buttons">
        {currentStep > 0 && (
          <button onClick={() => setCurrentStep(currentStep - 1)} className="nav-button secondary">
            ← Назад
          </button>
        )}

        <button onClick={nextStep} disabled={currentStep === 2 && !selectedPart} className="nav-button primary">
          {currentStep === steps.length - 1 ? "Завершить! 🎉" : "Далее →"}
        </button>
      </div>

      {/* Подсказка для интерактивных шагов */}
      {currentStep === 2 && !selectedPart && (
        <div className="hint-text">👆 Нажми на любую часть абакуса, чтобы продолжить!</div>
      )}
    </div>
  )
}

export default AbacusIntroduction
